import { useEffect, useState, useRef } from "react";
import { useAccount } from "src/contexts/accountContext";
// Client-only QuickLogin mode: no backend registration required.
import AgentAPI from "agent-api";
import { CircularProgress, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import "./quickLogin.css";
import { useEvents } from "src/contexts/eventsContext";
import BackendConfig from "src/backendConfig";
import { getQuickLoginServiceId } from "src/lib/neuroexchangeApi";
function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
}

function extractNameFromSignaturePayload(payload) {
  if (!payload) return "";

  const directFirst = payload.FIRST || payload.first || payload.First;
  const directLast = payload.LAST || payload.last || payload.Last;
  const directFull = payload.UserName || payload.username || payload.userName || payload.Name || payload.name;

  const propsRaw = payload.Properties || payload.properties || payload.Property || payload.property;

  const getFromObject = (obj, key) => {
    if (!obj || typeof obj !== 'object') return undefined;
    return obj[key] ?? obj[key?.toLowerCase?.()] ?? obj[key?.toUpperCase?.()];
  };

  const getFromArray = (arr, key) => {
    if (!Array.isArray(arr)) return undefined;
    const targetKey = String(key || '').toUpperCase();
    const found = arr.find((p) => {
      const name = p?.name ?? p?.Name ?? p?.key ?? p?.Key;
      return String(name || '').toUpperCase() === targetKey;
    });
    return found?.value ?? found?.Value;
  };

  const first =
    directFirst ||
    getFromObject(propsRaw, 'FIRST') ||
    getFromArray(propsRaw, 'FIRST') ||
    getFromObject(payload, 'FIRST') ||
    getFromArray(payload?.property, 'FIRST');

  const last =
    directLast ||
    getFromObject(propsRaw, 'LAST') ||
    getFromArray(propsRaw, 'LAST') ||
    getFromObject(payload, 'LAST') ||
    getFromArray(payload?.property, 'LAST');

  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || String(directFull || '').trim();
}

function getSignatureProperty(payload, key) {
  const targetKey = String(key || '').toUpperCase();
  const properties = payload?.Properties || payload?.properties || payload?.Property || payload?.property;

  if (Array.isArray(properties)) {
    const property = properties.find((item) =>
      String(item?.name ?? item?.Name ?? item?.key ?? item?.Key ?? '').toUpperCase() === targetKey
    );
    return property?.value ?? property?.Value;
  }

  if (properties && typeof properties === 'object') {
    const propertyKey = Object.keys(properties).find((name) => name.toUpperCase() === targetKey);
    if (propertyKey) return properties[propertyKey];
  }

  const directKey = Object.keys(payload || {}).find((name) => name.toUpperCase() === targetKey);
  return directKey ? payload[directKey] : undefined;
}

export default function QuickLogin({ neuron, purpose, active, onLoginSuccess }) {
  const { t } = useTranslation('common');
  const isDev = process.env.NODE_ENV === "development";
  const envNeuronHost = (process.env.REACT_APP_NEURON_HOST || "").trim();
  const QUICK_LOGIN_URL = BackendConfig.httpBackend + "/QuickLogin";
  const events = useEvents();
  // qrSrc: full URL from response.src (directly usable in <img>)
  const [qrSrc, setQrSrc] = useState();
  // signUrl: underlying tagsign:... value (may be useful for logging or future flows)
  const [signUrl, setSignUrl] = useState();
  const [serviceId, setServiceId] = useState();
  const { TabID: tabId, signStatus, startEvents, stopEvents } = useEvents();
  const [success, setSuccess] = useState(false);
  // No serviceId state needed in client-only mode.

  useEffect(() => {
    try {
      loadServiceId();

      const sp = new URLSearchParams(window.location.search);
      const debug = sp.get('debugHost') === '1' || window.localStorage?.getItem('debugHost') === '1';
      if (debug) {
        console.info('[QuickLogin][host-config]', {
          NODE_ENV: process.env.NODE_ENV,
          REACT_APP_NEURON_HOST: process.env.REACT_APP_NEURON_HOST,
          envNeuronHost,
          neuron: BackendConfig.neuronDomain,
          QUICK_LOGIN_URL,
          propNeuron: neuron,
        });
      }
    } catch (_) {
      // ignore
    }
  }, [envNeuronHost, BackendConfig.neuronDomain, QUICK_LOGIN_URL, neuron]);

  useEffect(() => {
    if (events) {
      events.eventEmitter.on("SignatureReceived", signatureRecived);
      events.eventEmitter.on("SignatureReceivedBE", signatureRecived);
      return () => {
        events.eventEmitter.off("SignatureReceived", signatureRecived);
        events.eventEmitter.off("SignatureReceivedBE", signatureRecived);
      };
    }
  }, [events]);

  const accountContext = useAccount();

  let displayInterval = null; // interval id (cleanup in effect)
  const socketRef = useRef(null);
  const pingRef = useRef(null);
  const resumePollRef = useRef(false);

  const xhttpEventHandler = () => {
    if (!tabId || resumePollRef.current) {
      return;
    }

    resumePollRef.current = true;
    const xhttp = new XMLHttpRequest();
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState === 4) {
        resumePollRef.current = false;

        if (xhttp.status === 200) {
          try {
            if (xhttp.responseText) {
              const pendingEvents = JSON.parse(xhttp.responseText);
              if (Array.isArray(pendingEvents)) {
                pendingEvents.forEach((event) => {
                  evaluateEvent(event);
                });
              } else if (pendingEvents) {
                evaluateEvent(pendingEvents);
              }
            }
          } catch (err) {
            console.log(xhttp.responseText);
            console.error(err);
          }
        } else {
          console.warn('[QuickLogin] Failed to fetch pending client events', xhttp.status, xhttp.responseText);
        }
      }
    };

    xhttp.open("POST", "/ClientEvents", true);
    xhttp.setRequestHeader("Content-Type", "text/plain");
    xhttp.setRequestHeader("X-TabID", tabId);
    xhttp.send(window.location.href);
  };

  const loadServiceId = async () => {
    setServiceId(await getQuickLoginServiceId());
  };

  const signatureRecived = async (signatureRecived) => {
    // An approved account/session is not necessarily an approved personal ID.
    // For Quick Login, require the identity fields as well as the approved state.
    const receivedState = String(signatureRecived?.State || "").trim();
    const firstName = String(getSignatureProperty(signatureRecived, 'FIRST') || '').trim();
    const lastName = String(getSignatureProperty(signatureRecived, 'LAST') || '').trim();
    const signatureEmail = String(getSignatureProperty(signatureRecived, 'EMAIL') || '').trim();
    const hasApprovedIdentity = receivedState.toLowerCase() === 'approved' && Boolean(firstName && lastName);
    const purchaseIdentityState = hasApprovedIdentity
      ? 'Approved'
      : receivedState.toLowerCase() === 'approved' ? 'IdentityRequired' : receivedState;
    accountContext?.setLegalIdState?.(purchaseIdentityState || null);
    try {
      if (purchaseIdentityState) window.sessionStorage.setItem("agent-legal-id-state", purchaseIdentityState);
      else window.sessionStorage.removeItem("agent-legal-id-state");
    } catch {
      // The in-memory state still gates purchases if storage is unavailable.
    }
    try {
      if (signatureEmail) window.sessionStorage.setItem('agent-user-email', signatureEmail);
      else window.sessionStorage.removeItem('agent-user-email');
    } catch { /* ignore unavailable storage */ }

    // Best-effort derive buyer display name from signature payload (FIRST/LAST) or fallback keys.
    const derivedName = extractNameFromSignaturePayload(signatureRecived);
    if (derivedName) {
      try {
        window.sessionStorage.setItem('agent-user-Username', derivedName);
        window.sessionStorage.setItem('username', derivedName);
      } catch {
        // silent
      }
      if (accountContext?.setUsername) {
        try { accountContext.setUsername(derivedName); } catch { /* silent */ }
      }
    }
    // Store legal id if present in event payload
    if (hasApprovedIdentity && signatureRecived?.Id && /@legal\./i.test(signatureRecived.Id)) {
      try {
        localStorage.setItem('agent-legal-id', signatureRecived.Id);
        localStorage.setItem('signatureRecived', JSON.stringify(signatureRecived));
        if (accountContext.setLegalId) {
          accountContext.setLegalId(signatureRecived.Id);
        }
      } catch (e) {
        console.warn('[QuickLogin] Failed to persist legalId', e);
      }
    } else {
      // Do not let an account-only event inherit an identity cached from a
      // previous Quick Login session.
      try {
        localStorage.removeItem('agent-legal-id');
        localStorage.removeItem('signatureRecived');
        window.sessionStorage.removeItem('agent-legal-id');
      } catch { /* ignore unavailable storage */ }
      accountContext?.setLegalId?.(undefined);
    }


    // Session mode: we received SignatureReceived/SignatureReceivedBE, so the neuron
    // has stored QuickLoginUser in the HTTP session. Mark user as authenticated
    // for client UI and flows. Persist minimal identity if available.
    console.log('[QuickLogin] Proceeding with session-auth: marking user authenticated without JWT');
    accountContext.setQuickLoggedIn(true);
    if (accountContext.setLoggedIn) {
      accountContext.setLoggedIn(true);
    }
    // Optional: mark session-auth for other parts of app to avoid JWT-required calls
    if (accountContext.setSessionAuth) {
      try { accountContext.setSessionAuth(true); } catch {
        // silent
      }
    }
    // Persist flags so navigation retains auth state
    try {
      window.sessionStorage.setItem('sessionAuth', 'true');
      window.sessionStorage.setItem('isQuickLoggedIn', 'true');
      window.sessionStorage.setItem('isLoggedIn', 'true');
    } catch {
      // silent
    }
    // Best-effort identity population from event (already derived above; keep legacy fallback)
    if (!derivedName && signatureRecived?.UserName) {
      try { window.sessionStorage.setItem('agent-user-Username', signatureRecived.UserName); } catch { /* silent */ }
      try { window.sessionStorage.setItem('username', signatureRecived.UserName); } catch { /* silent */ }
      if (accountContext?.setUsername) {
        try { accountContext.setUsername(signatureRecived.UserName); } catch { /* silent */ }
      }
    }
    // Do NOT call JWT-protected endpoints without a JWT to avoid 401s.
    // Balance and identities will be fetched later once a JWT exists.
    setSuccess(true);
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  const evaluateEvent = (event) => {
    if (event && event.type.match(/^[a-zA-Z0-9]+$/g)) {
      try {
        console.log(event.type);
        if (event.type === "SignatureReceived" || event.type === "SignatureReceivedBE") {
          signatureRecived(event.data);
        } else if (event.type === "signStatus") {
          console.log(event.type);
          signStatus(event);
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleQrClick = () => {
    if (!signUrl) return;

    try {
      window.location.assign(signUrl);
    } catch (err) {
      console.error('[QuickLogin] Failed to open sign URL', err);
    }
  };

  const displayQuickLogin = async () => {
    const xhttp = new XMLHttpRequest();
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState === 4 && xhttp.status === 200) {
        try {
          const data = JSON.parse(xhttp.responseText);
          setQrSrc(data.src);
          setSignUrl(data.signUrl);
        } catch (err) {
          console.error('[QuickLogin] Failed to parse QuickLogin response', err, xhttp.responseText);
        }
      }
    };

    console.log('[QuickLogin] Posting to QuickLogin URL:', QUICK_LOGIN_URL);
    xhttp.open("POST", QUICK_LOGIN_URL, true);

    // IMPORTANT: no credentials, no cookies
    // Either remove this line completely or explicitly set false:
    // xhttp.withCredentials = true;

    xhttp.setRequestHeader("Content-Type", "application/json");

    xhttp.send(
      JSON.stringify({
        mode: "image",
        purpose,
        serviceId: serviceId,     // let neuron create implicit serviceId
        tab: tabId,
        // agentApiTimeout: 3600   // ← THIS ENABLES SESSION-PROXY MODE + AgentApiToken
      })
    );
  };

  useEffect(() => {
    if (!tabId) {
      return undefined;
    }

    const resumeRealtimeEvents = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        stopEvents?.();
        startEvents?.(tabId);
      } catch (err) {
        console.warn('[QuickLogin] Failed to restart event listeners on resume', err);
      }

      xhttpEventHandler();

      if (active && serviceId && !success) {
        displayQuickLogin();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resumeRealtimeEvents();
      }
    };

    window.addEventListener('focus', resumeRealtimeEvents);
    window.addEventListener('pageshow', resumeRealtimeEvents);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', resumeRealtimeEvents);
      window.removeEventListener('pageshow', resumeRealtimeEvents);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [active, serviceId, startEvents, stopEvents, success, tabId]);

  // Periodically request new QR code (lifetime 5 minutes, poll every 2s)
  useEffect(() => {
    if (active && serviceId) {
      displayQuickLogin();
    }
    displayInterval = setInterval(() => {
      if (active && serviceId) {
        displayQuickLogin();
      }
    }, 2000);
    return () => {
      clearInterval(displayInterval);
      displayInterval = null;
    };
  }, [active, serviceId]);

  // Removed backend serviceId retrieval for client-only mode.

  return (
    <>
      <div className="quick-login-container">
        {qrSrc ? (
          !success ? (
            <img
              src={qrSrc}
              alt="Quick Login QR"
              onClick={handleQrClick}
              style={{ cursor: signUrl ? 'pointer' : 'default' }}
            />
          ) : null
        ) : (
          <>
            <CircularProgress />
            <Typography
              variant="body2"
              align="center"
              className="quick-login-text"
            >
              {t('auth.loadingQr', 'Loading the QR code')}
            </Typography>
          </>
        )}
      </div >
    </>
  );
}
